import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import logger from '../../utils/logger';
import { config } from '../../config/env';

interface Slide {
    id: string | number;
    imageUrl: string;
    title?: string;
    description?: string;
    categoryUrl?: string;
    isDecorative?: boolean;
    redirectUrl?: string;
}

interface HeroImage {
    id: string;
    url: string;
    alt?: string;
    order?: number;
    redirectUrl?: string;
}

const isExternalUrl = (url: string) => /^https?:\/\//i.test(url);

interface HomepageSettings {
    heroImages?: HeroImage[];
}

const HeroSlider: React.FC = () => {
    const functionalSlides: Slide[] = [
        {
            id: 'func-1',
            imageUrl: '/images/hero-1.webp',
            title: 'Нови процесори',
            description: 'Най-новите модели на специални цени',
            categoryUrl: '/category/protsesori',
            isDecorative: false
        },
        {
            id: 'func-2',
            imageUrl: '/images/hero-2.webp',
            title: 'Графични карти',
            description: 'Ускорете вашата система с нови графични карти',
            categoryUrl: '/category/video-karti',
            isDecorative: false
        },
        {
            id: 'func-3',
            imageUrl: '/images/hero-3.webp',
            title: 'Компютърни конфигурации',
            description: 'Готови решения за вашия дом или офис',
            categoryUrl: '/konfiguratsii',
            isDecorative: false
        },
        {
            id: 'func-4',
            imageUrl: '/images/hero-4.webp',
            title: 'Услуги',
            description: 'Професионални услуги за вашия компютър или лаптоп',
            categoryUrl: '/services',
            isDecorative: false
        }
    ];

    const [slides, setSlides] = useState<Slide[]>(functionalSlides);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);

    // The built-in slides render at once (they are the page's largest paint);
    // admin hero images are slotted in between them when this returns.
    useEffect(() => {
        const fetchHeroImages = async () => {
            try {
                const response = await fetch(`${config.apiUrl}/settings/homepage`);
                const data = await response.json();

                logger.log('[HeroSlider] API Response:', data);
                logger.log('[HeroSlider] Hero Images field:', data.heroImages);

                if (data.heroImages && data.heroImages.length > 0) {
                    // Convert hero images to slide format
                    const heroSlides: Slide[] = data.heroImages
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((image) => {
                            // Construct full URL for uploaded images
                            let imageUrl = image.url;
                            if (imageUrl && imageUrl.startsWith('/uploads/')) {
                                imageUrl = `${config.apiUrl}${imageUrl}`;
                            }
                            return {
                                id: `hero-${image.id}`,
                                imageUrl: imageUrl,
                                title: image.alt,
                                redirectUrl: image.redirectUrl,
                                isDecorative: true
                            };
                        });

                    logger.log('[HeroSlider] Converted hero slides:', heroSlides);

                    // Merge functional slides with hero images
                    // Alternate between functional and decorative slides
                    const mergedSlides: Slide[] = [];
                    const maxLength = Math.max(functionalSlides.length, heroSlides.length);

                    for (let i = 0; i < maxLength; i++) {
                        if (i < functionalSlides.length) {
                            mergedSlides.push(functionalSlides[i]);
                        }
                        if (i < heroSlides.length) {
                            mergedSlides.push(heroSlides[i]);
                        }
                    }

                    logger.log('[HeroSlider] Total slides after merge:', mergedSlides.length);
                    logger.log('[HeroSlider] Merged slides:', mergedSlides);

                    setSlides(mergedSlides);
                } else {
                    logger.log('[HeroSlider] No hero images found, using functional slides only');
                    // If no hero images, just use functional slides
                    setSlides(functionalSlides);
                }
            } catch (error) {
                logger.error('[HeroSlider] Error fetching:', error);
                // Fall back to functional slides on error silently
                setSlides(functionalSlides);
            }
        };

        fetchHeroImages();
    }, []);

    const nextSlide = () => {
        if (!isAnimating) {
            setIsAnimating(true);
            setCurrentIndex((prevIndex) => (prevIndex + 1) % slides.length);
            setTimeout(() => setIsAnimating(false), 10);
        }
    };

    const prevSlide = () => {
        if (!isAnimating) {
            setIsAnimating(true);
            setCurrentIndex((prevIndex) => (prevIndex - 1 + slides.length) % slides.length);
            setTimeout(() => setIsAnimating(false), 10);
        }
    };

    useEffect(() => {
        const interval = setInterval(() => {
            nextSlide();
        }, 5000);

        return () => clearInterval(interval);
    }, [currentIndex]);

    return (
        <div className="relative w-full h-64 md:h-80 overflow-hidden mb-8">
            <div
                className="flex transition-transform duration-500 ease-in-out h-full"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
                {slides.map((slide) => (
                    <div
                        key={slide.id}
                        className="min-w-full flex items-center justify-center bg-gray-200 relative"
                    >
                        <div
                            className="absolute inset-0 bg-center bg-cover bg-no-repeat"
                            style={{ backgroundImage: `url('${slide.imageUrl}')` }}
                        ></div>

                        {slide.isDecorative ? (
                            slide.redirectUrl ? (
                                // Clickable hero image (admin-configured redirect)
                                isExternalUrl(slide.redirectUrl) ? (
                                    <a
                                        href={slide.redirectUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="relative z-10 w-full h-full block"
                                    >
                                        {slide.title && (
                                            <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 text-white px-4 py-2 rounded">
                                                <p className="text-sm">{slide.title}</p>
                                            </div>
                                        )}
                                    </a>
                                ) : (
                                    <Link
                                        to={slide.redirectUrl}
                                        className="relative z-10 w-full h-full block"
                                    >
                                        {slide.title && (
                                            <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 text-white px-4 py-2 rounded">
                                                <p className="text-sm">{slide.title}</p>
                                            </div>
                                        )}
                                    </Link>
                                )
                            ) : (
                                // Decorative hero images - no link, just display
                                <div className="relative z-10 w-full h-full">
                                    {slide.title && (
                                        <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 text-white px-4 py-2 rounded">
                                            <p className="text-sm">{slide.title}</p>
                                        </div>
                                    )}
                                </div>
                            )
                        ) : (
                            // Functional slides with links
                            <Link to={slide.categoryUrl!} className="block w-full h-full">
                                <div className="relative z-10 text-center p-6 bg-black bg-opacity-50 text-white rounded-lg cursor-pointer hover:bg-opacity-70 transition-all w-full h-full flex flex-col items-center justify-center">
                                    <h2 className="text-2xl font-bold mb-2">{slide.title}</h2>
                                    <p>{slide.description}</p>
                                    <div className="mt-4 text-sm underline">
                                        Вижте още →
                                    </div>
                                </div>
                            </Link>
                        )}
                    </div>
                ))}
            </div>

            <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 p-2 rounded-full shadow-md hover:bg-opacity-100"
                aria-label="Предишен слайд"
            >
                <ChevronLeft className="h-6 w-6" />
            </button>

            <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 p-2 rounded-full shadow-md hover:bg-opacity-100"
                aria-label="Следващ слайд"
            >
                <ChevronRight className="h-6 w-6" />
            </button>

            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {slides.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentIndex(index)}
                        className={`w-3 h-3 rounded-full ${index === currentIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                            }`}
                        aria-label={`Слайд ${index + 1}`}
                    ></button>
                ))}
            </div>
        </div>
    );
};

export default HeroSlider;